/**
 * Authentic Momentum / Cyrus Prescod entity graph.
 *
 * Single source of truth for sameAs URLs, knowsAbout topics, and
 * the canonical Organization / Person identifiers. Imported by
 * every PageSeo consumer so the entity graph stays consistent
 * across /, /about, /cyrus-prescod, /framework, /press, /connect,
 * and every journal post.
 *
 * Only confirmed URLs live in `sameAs`. Surfaces still pending
 * an official link sit in `pending` and surface in /connect as
 * "Coming soon" rather than poisoning JSON-LD with broken links.
 */

export const SITE_URL = "https://authentic-momentum.com";
export const ORG_ID = `${SITE_URL}/#organization`;
export const CYRUS_ID = `${SITE_URL}/#cyrus`;

export const SOCIAL_PROFILES = {
  tiktok: {
    label: "TikTok",
    handle: "@authentic_momentum",
    url: "https://www.tiktok.com/@authentic_momentum",
    active: true,
  },
  instagram: {
    label: "Instagram",
    handle: "@authenticmomentum1",
    url: "https://www.instagram.com/authenticmomentum1/",
    active: true,
  },
  linkedin: {
    label: "LinkedIn",
    handle: "Cyrus Prescod",
    url: "https://www.linkedin.com/in/cyrus-prescod-b96344292/",
    active: true,
  },
  substack: {
    label: "Substack",
    handle: "@authenticmomentum",
    url: "https://substack.com/@authenticmomentum",
    active: true,
  },
  medium: {
    label: "Medium",
    handle: "@authenticmomentum",
    url: "https://medium.com/@authenticmomentum",
    active: true,
  },
  x: {
    label: "X",
    handle: "@AuthMomentum",
    url: "https://x.com/AuthMomentum",
    active: true,
  },
};

/**
 * sameAs array for JSON-LD. Filter out any profile that does not
 * have a confirmed URL so we never emit `null` or placeholder text
 * into a structured-data payload.
 */
export const SAME_AS = Object.values(SOCIAL_PROFILES)
  .filter((p) => p.active && p.url)
  .map((p) => p.url);

export const CYRUS_KNOWS_ABOUT = [
  "AI strategy",
  "Systems thinking",
  "Brand strategy",
  "Authentic Momentum framework",
  "Creative direction",
  "Identity-based design",
  "Founder positioning",
];

export const ORGANIZATION = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: "Authentic Momentum",
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}/assets/logo.png`,
  founder: { "@id": CYRUS_ID },
  sameAs: SAME_AS,
};

export const PERSON = {
  "@type": "Person",
  "@id": CYRUS_ID,
  name: "Cyrus Prescod",
  givenName: "Cyrus",
  familyName: "Prescod",
  jobTitle: "Founder, Strategist, Author",
  description:
    "Cyrus Prescod is the founder of Authentic Momentum, an AI strategist and systems builder helping founders and creators move from force into flow.",
  url: `${SITE_URL}/cyrus-prescod`,
  image: `${SITE_URL}/assets/brand/cyrus-portrait.webp`,
  knowsAbout: CYRUS_KNOWS_ABOUT,
  worksFor: { "@id": ORG_ID },
  sameAs: SAME_AS,
};
